import { OrderHelper } from '@orders.co/order-sdk';
import { v4 as uuidv4 } from 'uuid';

// ---------------------------------------------------------------------------
// Mock statuses & states
// ---------------------------------------------------------------------------
export const MOCK_STATUSES = [
  { _id: 'preview_draft', name: 'DRAFT', type: 'draft', sort: 0 },
  { _id: 'preview_new', name: 'NEW', type: 'new', sort: 1 },
  { _id: 'preview_confirmed', name: 'CONFIRMED', type: 'confirmed', sort: 2 },
];

export const MOCK_STATES = [
  { _id: 'preview_draft', name: 'DRAFT', type: 'draft', sort: 0 },
  { _id: 'preview_open', name: 'OPEN', type: 'open', sort: 1 },
];

// ---------------------------------------------------------------------------
// PathKey helpers
// ---------------------------------------------------------------------------
export const buildPathKey = (productId, mgId, parentOptionPathKey = null) => {
  if (parentOptionPathKey) {
    return `${parentOptionPathKey}__mg_${mgId}`;
  }
  return `product_${productId}__mg_${mgId}`;
};

export const buildOptionPathKey = (parentMgPathKey, optionId) => {
  return `${parentMgPathKey}__op_${optionId}`;
};

/**
 * Extract the parent option's pathKey from a currentOptionPathKey.
 * e.g. "product_x__mg_1__op_A__mg_2__op_B" → "product_x__mg_1__op_A"
 * If only one op_ segment or none → null (product level)
 */
export const extractParentOptionPathKey = (pathKey) => {
  if (!pathKey) return null;
  const segments = pathKey.split('__');
  // Find all op_ segment indices
  const opIndices = [];
  segments.forEach((seg, i) => {
    if (seg.startsWith('op_')) opIndices.push(i);
  });
  if (opIndices.length <= 1) return null;
  // Return path up to the second-to-last op_ segment (inclusive)
  const secondToLastOpIdx = opIndices[opIndices.length - 2];
  return segments.slice(0, secondToLastOpIdx + 1).join('__');
};

/**
 * Check if a pathKey contains a specific option ID in any op_ segment.
 */
export const pathKeyContainsOptionId = (pathKey, optionId) => {
  if (!pathKey || !optionId) return false;
  const segments = pathKey.split('__');
  return segments.some(
    (seg) => seg === `op_${optionId}` || seg === `option_${optionId}`
  );
};

// ---------------------------------------------------------------------------
// Price type calculation – exact copy from POS client priceTypeCalculator.ts
// ---------------------------------------------------------------------------
export const PRICE_TYPES = {
  MULTIPLIER_APPLYING_TO_PARENT_PRICE: 'multiplierApplyingToParentPrice',
  MULTIPLIER_APPLYING_TO_NESTED_PRICE: 'multiplierApplyingToNestedPrice',
  FIX_PRICE: 'fixPrice',
};

// ---------------------------------------------------------------------------
// Multiplier condition helpers – mirrors POS client itemQuontity.ts
// ---------------------------------------------------------------------------
export const isMultiplierConditionActive = (cond) =>
  !!cond?.applyConditionWithMultiplier &&
  (cond.priceType === PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE ||
    cond.priceType === PRICE_TYPES.MULTIPLIER_APPLYING_TO_NESTED_PRICE);

export const getEffectiveOptionsCount = (
  options,
  applyValidationToItemsActualQuantity,
  multiplierCondition,
  mgPathKey,
  selectedModifierGroups
) => {
  const nonNull = (options || []).filter((opt) => opt != null);
  if (isMultiplierConditionActive(multiplierCondition)) {
    return nonNull.reduce(
      (sum, opt) =>
        sum + (opt.count ?? 1) * (opt.originalPrice ?? opt.price ?? 0),
      0
    );
  }
  if (!applyValidationToItemsActualQuantity) {
    return nonNull.length;
  }
  return nonNull.reduce((sum, opt) => {
    if (mgPathKey && selectedModifierGroups && opt?._id) {
      return (
        sum +
        computeEffectiveActualQuantity(
          opt._id,
          mgPathKey,
          selectedModifierGroups
        )
      );
    }
    return sum + (opt?.actualQuantityWithMultiplier || opt?.count || 1);
  }, 0);
};

export const getEffectiveOptionCount = (
  option,
  applyValidationToItemsActualQuantity,
  multiplierCondition,
  mgPathKey,
  selectedModifierGroups
) => {
  if (option == null) return 0;
  if (isMultiplierConditionActive(multiplierCondition)) {
    return (option.count ?? 1) * (option.originalPrice ?? option.price ?? 0);
  }
  if (!applyValidationToItemsActualQuantity) {
    return option.count ?? 1;
  }
  if (mgPathKey && selectedModifierGroups && option._id) {
    return computeEffectiveActualQuantity(
      option._id,
      mgPathKey,
      selectedModifierGroups
    );
  }
  return option.actualQuantityWithMultiplier ?? option.count ?? 1;
};

export const computeTotalItemCount = (
  options,
  applyValidationToItemsActualQuantity,
  multiplierCondition,
  mgPathKey,
  selectedModifierGroups
) =>
  (options || []).reduce((sum, opt) => {
    if (opt == null) return sum;
    return (
      sum +
      getEffectiveOptionCount(
        opt,
        applyValidationToItemsActualQuantity,
        multiplierCondition,
        mgPathKey,
        selectedModifierGroups
      )
    );
  }, 0);

const asBool = (value, fallback = false) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === '1' || normalized === 'true') return true;
    if (normalized === '0' || normalized === 'false') return false;
  }
  return fallback;
};

// Min item rules only apply when canSelectMultipleOptions is enabled, since
// options cannot exceed a count of 1 without it.
export const hasModifierItemMinimums = (mgDef) =>
  asBool(mgDef?.canSelectMultipleOptions, false) &&
  ((Number(mgDef?.minTotalNumberOfModifierItems) || 0) > 0 ||
    (Number(mgDef?.minAnySingleModifierItems) || 0) > 0);

// minAnySingleModifierItems: every selected option must reach it.
// minTotalNumberOfModifierItems: total item count must reach it, even when
// nothing is selected.
export const meetsModifierItemMinimums = (mgDef, options) => {
  if (!hasModifierItemMinimums(mgDef)) return true;

  const multiplierCond = {
    applyConditionWithMultiplier: mgDef.applyConditionWithMultiplier,
    priceType: mgDef.priceType,
  };

  const minTotalItems = Number(mgDef.minTotalNumberOfModifierItems) || 0;
  if (
    minTotalItems > 0 &&
    computeTotalItemCount(
      options,
      mgDef.applyValidationToItemsActualQuantity,
      multiplierCond
    ) < minTotalItems
  ) {
    return false;
  }

  const minAnySingle = Number(mgDef.minAnySingleModifierItems) || 0;
  if (minAnySingle > 0) {
    for (const opt of options || []) {
      if (opt == null) continue;
      const count = getEffectiveOptionCount(
        opt,
        mgDef.applyValidationToItemsActualQuantity,
        multiplierCond
      );
      if (count < minAnySingle) return false;
    }
  }

  return true;
};

export const findAllMgInstances = (allModifierGroups, mgId) => {
  const extractMgId = (pk) => {
    const parts = pk.split('__');
    const last = parts[parts.length - 1];
    return last?.startsWith('mg_') ? last.slice(3) : null;
  };
  return Object.keys(allModifierGroups || {}).filter(
    (pk) => extractMgId(pk) === mgId
  );
};

export const computeParentActualQuantity = (
  parentCalculatedActualQuantity,
  multiplierChildren
) => {
  if (!multiplierChildren || multiplierChildren.length === 0) {
    return parentCalculatedActualQuantity;
  }
  const sum = multiplierChildren.reduce(
    (acc, c) => acc + (c.count ?? 1) * (c.originalPrice ?? 0),
    0
  );
  return parentCalculatedActualQuantity * sum;
};

// ---------------------------------------------------------------------------
// Actual quantity propagation — walk up MANP/MATP parents to find nearest
// fixPrice ancestor and compute actualQuantityWithMultiplier.
// ---------------------------------------------------------------------------

const extractGrandparentFromMgPathKey = (mgPathKey) => {
  const segments = mgPathKey.split('__');
  segments.pop(); // remove last mg_XXX
  const lastSeg = segments[segments.length - 1];
  if (!lastSeg || !lastSeg.startsWith('op_')) {
    return null;
  }
  const optionId = lastSeg.replace('op_', '');
  segments.pop(); // remove op_XXX
  return { mgPathKey: segments.join('__'), optionId };
};

const findFixPriceAncestorFromPathKey = (
  parentMgPathKey,
  parentOptionId,
  selectedModifierGroups,
  menuItems,
  menuModifierGroups
) => {
  let currentMgPathKey = parentMgPathKey;
  let currentOptionId = parentOptionId;
  let accumulatedPrice = 1;
  let isImmediate = true;

  while (true) {
    const menuItem = menuItems[currentOptionId];
    const mgId = extractMgIdFromPathKey(currentMgPathKey);
    const menuMg = mgId ? menuModifierGroups[mgId] : null;
    const effectivePriceType =
      menuItem && menuMg ? getEffectivePriceType(menuItem, menuMg) : null;

    if (
      effectivePriceType === null ||
      effectivePriceType === PRICE_TYPES.FIX_PRICE
    ) {
      const targetOption =
        selectedModifierGroups[currentMgPathKey]?.modifierOptions?.[
          currentOptionId
        ];
      return {
        targetPathKey: currentMgPathKey,
        targetOptionId: currentOptionId,
        targetOption,
        accumulatedManpPrice: accumulatedPrice,
        isImmediate,
      };
    }

    const optOriginalPrice = menuItem?.price ?? 1;
    accumulatedPrice *= optOriginalPrice;
    isImmediate = false;

    const grandparentInfo = extractGrandparentFromMgPathKey(currentMgPathKey);
    if (!grandparentInfo) {
      return null;
    }

    currentMgPathKey = grandparentInfo.mgPathKey;
    currentOptionId = grandparentInfo.optionId;
  }
};

export const computeActualQtyUpdate = ({
  parentMgPathKey,
  parentOptionId,
  parentBaseActualQty,
  multiplierChildren,
  selectedModifierGroups,
  menuItems,
  menuModifierGroups,
}) => {
  const ancestorInfo = findFixPriceAncestorFromPathKey(
    parentMgPathKey,
    parentOptionId,
    selectedModifierGroups,
    menuItems,
    menuModifierGroups
  );

  if (!ancestorInfo || ancestorInfo.isImmediate) {
    const result = computeParentActualQuantity(
      parentBaseActualQty,
      multiplierChildren
    );
    return {
      parentActualQty: result,
      ancestorDispatch: null,
    };
  }

  const immediateResult = computeParentActualQuantity(
    parentBaseActualQty,
    multiplierChildren
  );

  const ancestorBaseActualQty =
    ancestorInfo.targetOption?.calculatedActualQuantity ??
    ancestorInfo.targetOption?.actualQuantityWithMultiplier ??
    ancestorInfo.targetOption?.count ??
    1;

  const parentMg = selectedModifierGroups[parentMgPathKey];
  const parentMgOpts = parentMg?.modifierOptions ?? {};
  let siblingSum = 0;
  let currentFound = false;
  for (const [key, opt] of Object.entries(parentMgOpts)) {
    const optId = opt?._id ?? key;
    if (optId === parentOptionId || key === parentOptionId) {
      siblingSum += immediateResult;
      currentFound = true;
    } else {
      const contrib = opt?.actualQuantityWithMultiplier || opt?.count || 1;
      siblingSum += contrib;
    }
  }
  if (!currentFound) {
    siblingSum += immediateResult;
  }

  const ancestorActualQty = ancestorBaseActualQty * siblingSum;

  return {
    parentActualQty: immediateResult,
    ancestorDispatch: {
      pathKey: ancestorInfo.targetPathKey,
      optionId: ancestorInfo.targetOptionId,
      actualQuantityWithMultiplier: ancestorActualQty,
    },
  };
};

/**
 * Apply the result of computeActualQtyUpdate to updatedGroups (web app state).
 * If ancestorDispatch is present, updates the ancestor option directly in state.
 */
export const applyAncestorDispatch = (updatedGroups, aqResult) => {
  if (!aqResult.ancestorDispatch) return;
  const { pathKey, optionId } = aqResult.ancestorDispatch;
  if (updatedGroups[pathKey]?.modifierOptions?.[optionId]) {
    updatedGroups[pathKey] = {
      ...updatedGroups[pathKey],
      modifierOptions: {
        ...updatedGroups[pathKey].modifierOptions,
        [optionId]: {
          ...updatedGroups[pathKey].modifierOptions[optionId],
          actualQuantityWithMultiplier: 0,
        },
      },
    };
  }
};

/**
 * Compute effective actualQuantityWithMultiplier for a fixPrice option.
 *
 * Walks all descendant MGs and finds MATP options. Only "leaf" MATP options
 * (those with no further MATP children) are summed, because their
 * actualQuantityWithMultiplier already encodes the full multiplier chain.
 *
 * If no MATP descendants exist, returns the option's own actualQuantityWithMultiplier.
 */
export const computeEffectiveActualQuantity = (
  optionId,
  mgPathKey,
  selectedModifierGroups
) => {
  const mg = selectedModifierGroups[mgPathKey];
  const option = mg?.modifierOptions?.[optionId];
  const baseAQ = option?.actualQuantityWithMultiplier || option?.count || 1;

  const optionPathKey = `${mgPathKey}__op_${optionId}`;
  const prefix = optionPathKey + '__';

  const matpOptions = [];

  for (const [pk, descendantMg] of Object.entries(selectedModifierGroups)) {
    if (!pk.startsWith(prefix)) {
      continue;
    }
    const mgPriceType = descendantMg?.priceType ?? null;
    const opts = descendantMg?.modifierOptions ?? {};
    for (const [optKey, opt] of Object.entries(opts)) {
      const optPriceType = opt?.priceType ?? mgPriceType;
      if (optPriceType === PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE) {
        matpOptions.push({
          fullPath: `${pk}__op_${opt._id ?? optKey}`,
          pk,
          actualQty:
            opt?.actualQuantityWithMultiplier ||
            opt?.calculatedActualQuantity ||
            opt?.count ||
            0,
        });
      }
    }
  }

  if (matpOptions.length === 0) {
    return baseAQ;
  }

  const leafMatp = matpOptions.filter(
    (opt) =>
      !matpOptions.some((other) => other.pk.startsWith(opt.fullPath + '__'))
  );

  return leafMatp.reduce((sum, o) => sum + o.actualQty, 0);
};

/**
 * Apply effective AQ to all options in a modifier group before free recalculation.
 */
export const applyEffectiveAQ = (pathKey, opts, applyValidation, allGroups) => {
  if (!applyValidation) return;
  for (const [k, o] of Object.entries(opts)) {
    if (o) {
      o.actualQuantityWithMultiplier = computeEffectiveActualQuantity(
        o._id ?? k,
        pathKey,
        allGroups
      );
    }
  }
};

// ---------------------------------------------------------------------------
// Ancestor MG max constraint (nested multiplierApplyingToParentPrice chains)
// ---------------------------------------------------------------------------

const getParentMgPathKey = (pathKey) => {
  const parts = pathKey.split('__').filter(Boolean);
  if (parts.length <= 2) return null;
  return parts.slice(0, -2).join('__');
};

const extractMgIdFromPathKey = (pathKey) => {
  const parts = pathKey.split('__').filter(Boolean);
  const last = parts[parts.length - 1];
  return last?.startsWith('mg_') ? last.slice(3) : null;
};

const extractOptionIdFromPath = (pathKey) => {
  if (!pathKey) return null;
  const parts = pathKey.split('__').filter(Boolean);
  for (let i = parts.length - 1; i >= 0; i--) {
    if (parts[i].startsWith('op_')) return parts[i].slice(3);
    if (parts[i].startsWith('option_')) return parts[i].slice(7);
  }
  return null;
};

const mgHasMultiplierToParentOptions = (mg, items) => {
  if (mg?.priceType === PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE) {
    return true;
  }
  const optionIds = mg?.optionIds || [];
  return optionIds.some((oid) => {
    const opt = items?.[oid];
    if (!opt) return false;
    return (
      getEffectivePriceType(opt, mg) ===
      PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE
    );
  });
};

export const findAncestorMgWithMaxConstraint = (
  currentMgPathKey,
  _selectedGroups,
  menuGroups,
  items
) => {
  let current = currentMgPathKey;

  while (current) {
    const parentMgPathKey = getParentMgPathKey(current);
    if (!parentMgPathKey) break;

    const connectingOptionId = extractParentOptionId(current);
    if (!connectingOptionId) break;

    const parentMgId = extractMgIdFromPathKey(parentMgPathKey);
    const parentMg = parentMgId ? menuGroups[parentMgId] : null;
    if (!parentMg) break;

    const connectingOption = items?.[connectingOptionId];
    const effectivePT = connectingOption
      ? getEffectivePriceType(connectingOption, parentMg)
      : null;

    if (effectivePT !== PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE) {
      if (parentMg.max != null && Number(parentMg.max) > 0) {
        return { ancestorPathKey: parentMgPathKey, ancestorMg: parentMg };
      }
      return null;
    }

    current = parentMgPathKey;
  }
  return null;
};

export const computeMultiplierChainDown = (
  optionId,
  optionPathKey,
  selectedGroups,
  menuGroups,
  items,
  currentMgPathKeyForZero = null
) => {
  const optionItem = items?.[optionId];
  const nestedMgIds = optionItem?.modifierGroupIds;
  if (!nestedMgIds?.length) return 1.0;

  let sum = 0;
  let hasMultiplierMg = false;
  for (const nestedMgId of nestedMgIds) {
    const mg = menuGroups?.[nestedMgId];
    if (!mg || !mgHasMultiplierToParentOptions(mg, items)) continue;
    hasMultiplierMg = true;
    const nestedMgPathKey = `${optionPathKey}__mg_${nestedMgId}`;
    const nestedSelected = selectedGroups[nestedMgPathKey];
    const options = nestedSelected?.modifierOptions ?? {};

    const activeOptions = Object.entries(options).filter(
      ([, o]) => o && (o.count ?? 0) > 0
    );
    if (activeOptions.length === 0) {
      if (
        currentMgPathKeyForZero &&
        nestedMgPathKey === currentMgPathKeyForZero
      ) {
        return 0;
      }
      sum += 1.0;
      continue;
    }
    for (const [optId, optData] of activeOptions) {
      const opt = optData;
      const optItem = items?.[optId];
      const effectivePT = optItem ? getEffectivePriceType(optItem, mg) : null;
      if (effectivePT !== PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE)
        continue;
      const optPathKey = `${nestedMgPathKey}__op_${optId}`;
      const nestedChain = computeMultiplierChainDown(
        optId,
        optPathKey,
        selectedGroups,
        menuGroups,
        items,
        currentMgPathKeyForZero
      );
      const price = opt.originalPrice ?? opt.price ?? 0;
      sum += (opt.count ?? 0) * price * nestedChain;
    }
  }
  if (!hasMultiplierMg) return 1.0;
  return sum > 0 ? sum : 1.0;
};

export const computeAncestorEffectiveTotal = (
  ancestorPathKey,
  selectedGroups,
  menuGroups,
  items,
  currentMgPathKey = null
) => {
  const ancestorSelected = selectedGroups[ancestorPathKey];
  const options = ancestorSelected?.modifierOptions ?? {};
  let total = 0;
  for (const [optId, optData] of Object.entries(options)) {
    const opt = optData;
    if (!opt || (opt.count ?? 0) <= 0) continue;
    const optPathKey = `${ancestorPathKey}__op_${optId}`;
    const chainDown = computeMultiplierChainDown(
      optId,
      optPathKey,
      selectedGroups,
      menuGroups,
      items,
      currentMgPathKey
    );
    total += (opt.count ?? 0) * chainDown;
  }
  return total;
};

export const computeChainMultiplierUp = (
  currentMgPathKey,
  ancestorPathKey,
  selectedGroups,
  menuGroups,
  items
) => {
  if (currentMgPathKey === ancestorPathKey) {
    return { chainMultiplier: 1, ancestorOptionId: null };
  }
  let product = 1;
  let current = currentMgPathKey;
  let ancestorOptionId = null;

  while (current && current !== ancestorPathKey) {
    const parentMgPk = getParentMgPathKey(current);
    if (!parentMgPk) break;
    const optionId = extractOptionIdFromPath(current);
    if (optionId) {
      const parentMgId = extractMgIdFromPathKey(parentMgPk);
      const parentMg = parentMgId ? menuGroups[parentMgId] : null;
      const optItem = items?.[optionId];
      const effectivePT =
        optItem && parentMg ? getEffectivePriceType(optItem, parentMg) : null;

      if (effectivePT === PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE) {
        const parentMgSelected = selectedGroups[parentMgPk];
        const optData = parentMgSelected?.modifierOptions?.[optionId];
        const price = optData?.originalPrice ?? optData?.price ?? 1;
        product *= price;
      }
      if (parentMgPk === ancestorPathKey) {
        ancestorOptionId = optionId;
      }
    }
    current = parentMgPk;
  }
  return { chainMultiplier: product, ancestorOptionId };
};

export const shouldDisableForAncestorMax = (
  candidateOptionId,
  currentMgPathKey,
  selectedGroups,
  menuGroups,
  items
) => {
  const ancestor = findAncestorMgWithMaxConstraint(
    currentMgPathKey,
    selectedGroups,
    menuGroups,
    items
  );
  if (!ancestor) return false;

  const { ancestorPathKey, ancestorMg } = ancestor;
  if (ancestorPathKey === currentMgPathKey) return false;
  const maxVal = Number(ancestorMg.max) || 0;
  if (maxVal <= 0) return false;

  if (!ancestorMg.applyValidationToItemsActualQuantity) {
    return false;
  }

  const currentMgId = extractMgIdFromPathKey(currentMgPathKey);
  const currentMgData = currentMgId ? menuGroups[currentMgId] : null;
  const candidateItem = items?.[candidateOptionId];
  const candidateEffectivePT =
    candidateItem && currentMgData
      ? getEffectivePriceType(candidateItem, currentMgData)
      : null;
  if (
    candidateEffectivePT !== PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE
  ) {
    return false;
  }

  const ancestorParentOptionId = extractParentOptionId(ancestorPathKey);
  const ancestorParentOption = ancestorParentOptionId
    ? items?.[ancestorParentOptionId]
    : null;
  const rawPlacementPrice =
    ancestorParentOption?.originalPrice ?? ancestorParentOption?.price ?? 1;
  const effectivePlacementPrice = ancestorMg.conditionIsAppliedForProduct
    ? rawPlacementPrice > 0
      ? rawPlacementPrice
      : 1
    : 1;

  let currentTotal;
  if (ancestorMg.conditionIsAppliedForProduct) {
    const ancestorMgId = extractMgIdFromPathKey(ancestorPathKey);
    const allAncestorPaths = ancestorMgId
      ? findAllMgInstances(selectedGroups, ancestorMgId)
      : [ancestorPathKey];
    currentTotal = allAncestorPaths.reduce((sum, pk) => {
      const pkParentOptionId = extractParentOptionId(pk);
      const pkParentOption = pkParentOptionId
        ? items?.[pkParentOptionId]
        : null;
      const pkRaw = pkParentOption?.originalPrice ?? pkParentOption?.price ?? 1;
      const pkPlacementPrice = pkRaw > 0 ? pkRaw : 1;
      return (
        sum +
        pkPlacementPrice *
          computeAncestorEffectiveTotal(
            pk,
            selectedGroups,
            menuGroups,
            items,
            currentMgPathKey
          )
      );
    }, 0);
  } else {
    currentTotal = computeAncestorEffectiveTotal(
      ancestorPathKey,
      selectedGroups,
      menuGroups,
      items,
      currentMgPathKey
    );
  }

  const { chainMultiplier, ancestorOptionId } = computeChainMultiplierUp(
    currentMgPathKey,
    ancestorPathKey,
    selectedGroups,
    menuGroups,
    items
  );
  if (ancestorOptionId == null) return false;

  const candidateOption = items?.[candidateOptionId];
  const candidatePrice =
    candidateOption?.originalPrice ?? candidateOption?.price ?? 1;

  const ancestorSelected = selectedGroups[ancestorPathKey];
  const ancestorOptions = ancestorSelected?.modifierOptions ?? {};
  const ancestorOption = ancestorOptionId
    ? ancestorOptions[ancestorOptionId]
    : null;
  const ancestorOptionCount = ancestorOption?.count ?? 0;

  const hypotheticalContribution =
    effectivePlacementPrice *
    ancestorOptionCount *
    chainMultiplier *
    candidatePrice;
  const currentBranchContribution = ancestorOptionId
    ? effectivePlacementPrice *
      (ancestorOptionCount || 0) *
      computeMultiplierChainDown(
        ancestorOptionId,
        `${ancestorPathKey}__op_${ancestorOptionId}`,
        selectedGroups,
        menuGroups,
        items,
        currentMgPathKey
      )
    : 0;

  const newTotal =
    currentTotal - currentBranchContribution + hypotheticalContribution;
  return newTotal > maxVal;
};

// ---------------------------------------------------------------------------
// Free-options recalculation — wraps OrderHelper.recalculateFreeOptions /
// recalculateFreeOptionsAggregated for use from the preview reducer.
// ---------------------------------------------------------------------------
export const recalculateFreeForGroups = (groups) => {
  const result = { ...groups };
  const handledMgIds = new Set();

  Object.entries(result).forEach(([key, group]) => {
    if (!group || group.free == null || group.free === 0) {
      return;
    }
    const mgId = group._id;

    if (group.conditionIsAppliedForProduct && mgId && !handledMgIds.has(mgId)) {
      handledMgIds.add(mgId);
      const instancePKs = findAllMgInstances(result, mgId);
      const instances = instancePKs.map((pk) => ({
        pathKey: pk,
        options: result[pk]?.modifierOptions || {},
      }));
      for (const inst of instances) {
        applyEffectiveAQ(
          inst.pathKey,
          inst.options,
          group.applyValidationToItemsActualQuantity,
          result
        );
      }
      try {
        const updated = OrderHelper.recalculateFreeOptionsAggregated(
          instances,
          group.free || 0,
          group.applyValidationToItemsActualQuantity
        );
        for (const [pk, opts] of Object.entries(updated)) {
          if (result[pk]) {
            result[pk] = { ...result[pk], modifierOptions: opts };
          }
        }
      } catch (_) {
        instancePKs.forEach((pk) => {
          if (result[pk]) {
            try {
              result[pk] = {
                ...result[pk],
                modifierOptions: OrderHelper.recalculateFreeOptions(
                  result[pk].modifierOptions,
                  group.free || 0,
                  group.applyValidationToItemsActualQuantity
                ),
              };
            } catch (_e) {
            }
          }
        });
      }
    } else if (!group.conditionIsAppliedForProduct) {
      applyEffectiveAQ(
        key,
        group.modifierOptions,
        group.applyValidationToItemsActualQuantity,
        result
      );
      try {
        result[key] = {
          ...group,
          modifierOptions: OrderHelper.recalculateFreeOptions(
            group.modifierOptions,
            group.free || 0,
            group.applyValidationToItemsActualQuantity
          ),
        };
      } catch (_) {
      }
    }
  });

  return result;
};

/**
 * InOrderViewLayout enum values - controls how options join with parent name in order views
 */
export const IN_ORDER_VIEW_LAYOUTS = {
  JOIN_PARENT_FROM_LEFT: 'joinParentFromLeft',
  JOIN_PARENT_FROM_RIGHT: 'joinParentFromRight',
};

// ---------------------------------------------------------------------------
// modifierTreeBuilder helpers – exact copy from POS client modifierTreeBuilder.ts
// ---------------------------------------------------------------------------

/** Gets the nesting level from a pathKey. Level = count("mg_") - 1 */
export const getPathKeyLevel = (pathKey) => {
  if (!pathKey) return 0;
  const mgCount = (pathKey.match(/mg_/g) || []).length;
  return Math.max(0, mgCount - 1);
};

/** Extract parent option ID from pathKey (last op_ segment) */
export const extractParentOptionId = (pathKey) => {
  if (!pathKey) return null;
  const parts = pathKey.split('__').filter(Boolean);
  for (let i = parts.length - 1; i >= 0; i--) {
    if (parts[i].startsWith('op_') || parts[i].startsWith('option_')) {
      if (parts[i].startsWith('op_')) return parts[i].slice(3);
      return parts[i].slice(7);
    }
  }
  return null;
};

/** Extract raw ID from prefixed segment */
export const extractIdFromSegment = (segment) => {
  if (segment.startsWith('product_')) return segment.slice(8);
  if (segment.startsWith('op_')) return segment.slice(3);
  if (segment.startsWith('option_')) return segment.slice(7);
  if (segment.startsWith('mg_')) return segment.slice(3);
  return segment;
};

/** Extract the parent modifier group ID from pathKey */
export const extractParentModifierGroupId = (pathKey) => {
  if (!pathKey) return null;
  const parts = pathKey.split('__').filter(Boolean);
  for (let i = parts.length - 1; i >= 0; i--) {
    const isOptionSegment =
      parts[i].startsWith('op_') || parts[i].startsWith('option_');
    if (isOptionSegment && i > 0) {
      const prevPart = parts[i - 1];
      if (prevPart.startsWith('mg_')) {
        return extractIdFromSegment(prevPart);
      }
    }
  }
  return null;
};

/**
 * Get the effective priceType: option level overrides MG level.
 */
export const getEffectivePriceType = (option, modifierGroup) => {
  const optPT = option?.priceType;
  if (optPT !== null && optPT !== undefined && optPT !== '') return optPT;
  const mgPT = modifierGroup?.priceType;
  if (mgPT !== null && mgPT !== undefined && mgPT !== '') return mgPT;
  return null;
};

/**
 * Get the effective inOrderViewLayout for an option.
 * Returns the option's inOrderViewLayout if set, otherwise falls back to the modifier group's inOrderViewLayout.
 */
export const getEffectiveInOrderViewLayout = (option, modifierGroup) => {
  if (
    option?.inOrderViewLayout !== undefined &&
    option?.inOrderViewLayout !== null
  ) {
    return option.inOrderViewLayout;
  }
  if (
    modifierGroup?.inOrderViewLayout !== undefined &&
    modifierGroup?.inOrderViewLayout !== null
  ) {
    return modifierGroup.inOrderViewLayout;
  }
  return null;
};

/**
 * Calculate option price based on priceType.
 */
export const calculatePriceByType = ({
  option,
  modifierGroup,
  parentOption = null,
  isNestedUnderParentOption = false,
}) => {
  const originalPrice = option?.price ?? 0;
  const effectivePriceType = getEffectivePriceType(option, modifierGroup);

  let optionPrice = originalPrice;
  let shouldZeroParentPrice = false;
  const optionCount = option?.count ?? 1;
  let actualQuantityWithMultiplier = optionCount;

  if (effectivePriceType === PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE) {
    if (isNestedUnderParentOption && parentOption) {
      const parentPriceToUse =
        parentOption.calculatedPrice ??
        (parentOption.price !== 0
          ? parentOption.price
          : parentOption.originalPrice) ??
        0;
      optionPrice = parentPriceToUse * originalPrice;
      shouldZeroParentPrice = true;
      const parentActualQtyToUse =
        parentOption.calculatedActualQuantity ??
        parentOption.actualQuantityWithMultiplier ??
        optionCount;
      actualQuantityWithMultiplier = parentActualQtyToUse * originalPrice;
    } else {
      optionPrice = 0;
    }
  } else if (
    effectivePriceType === PRICE_TYPES.MULTIPLIER_APPLYING_TO_NESTED_PRICE
  ) {
    optionPrice = 0;
    actualQuantityWithMultiplier = originalPrice * optionCount;
  } else {
    // fixPrice or null (default)
    if (
      isNestedUnderParentOption &&
      parentOption?.priceType ===
        PRICE_TYPES.MULTIPLIER_APPLYING_TO_NESTED_PRICE
    ) {
      const parentOrigPrice =
        parentOption.originalPrice ?? parentOption.price ?? 0;
      optionPrice = originalPrice * parentOrigPrice;
      actualQuantityWithMultiplier = parentOrigPrice * optionCount;
    }
  }

  return {
    optionPrice,
    originalPrice,
    calculatedPrice: optionPrice,
    shouldZeroParentPrice,
    effectivePriceType,
    actualQuantityWithMultiplier,
  };
};

// ---------------------------------------------------------------------------
// Cascade removal – delete all modifier group entries whose pathKey
// contains a given option ID or starts with a prefix.
// ---------------------------------------------------------------------------
export const removeModifierGroupsByOptionId = (groups, optionId) => {
  const updated = { ...groups };
  Object.keys(updated).forEach((pk) => {
    if (pathKeyContainsOptionId(pk, optionId)) {
      delete updated[pk];
    }
  });
  return updated;
};

export const removeNestedGroupsForPrefix = (groups, prefix) => {
  const updated = { ...groups };
  Object.keys(updated).forEach((pk) => {
    if (pk.startsWith(prefix + '__')) {
      delete updated[pk];
    }
  });
  return updated;
};

// ---------------------------------------------------------------------------
// Build an option entry with proper price + metadata
// ---------------------------------------------------------------------------
export const buildOptionEntry = ({
  optionItem,
  menuMap,
  modifierGroup,
  parentOption,
  isNestedUnderParentOption,
  optionPathKey,
}) => {
  // Source priceType from menu item first, then fall back to optionItem
  const menuOptionItem = menuMap?.items?.[optionItem._id];
  // Source priceType from menu modifier group as well
  const menuMg = menuMap?.modifierGroups?.[modifierGroup?._id];

  const optionForCalc = {
    ...optionItem,
    price: menuOptionItem?.price ?? optionItem.price ?? 0,
    priceType: menuOptionItem?.priceType ?? optionItem.priceType ?? undefined,
  };

  const mgForCalc = {
    ...modifierGroup,
    priceType: menuMg?.priceType ?? modifierGroup?.priceType ?? undefined,
  };

  const priceResult = calculatePriceByType({
    option: optionForCalc,
    modifierGroup: mgForCalc,
    parentOption,
    isNestedUnderParentOption,
  });

  return {
    entry: {
      ...optionItem,
      count: 1,
      price: priceResult.optionPrice,
      originalPrice: priceResult.originalPrice,
      calculatedPrice: priceResult.calculatedPrice,
      priceType: priceResult.effectivePriceType,
      actualQuantityWithMultiplier: priceResult.actualQuantityWithMultiplier,
      calculatedActualQuantity: priceResult.actualQuantityWithMultiplier,
      hideNameOnOrder:
        menuOptionItem?.hideNameOnOrder ?? optionItem.hideNameOnOrder,
      inOrderViewLayout:
        menuOptionItem?.inOrderViewLayout ?? optionItem.inOrderViewLayout,
      pathKey: optionPathKey,
    },
    priceResult,
  };
};

// ---------------------------------------------------------------------------
// Resolve parent option info from navigation state
// ---------------------------------------------------------------------------
export const resolveParentOptionInfo = (
  currentOptionPathKey,
  modifierGroups
) => {
  if (!currentOptionPathKey) return { parentOption: null, isNested: false };

  const segments = currentOptionPathKey.split('__');
  // Find the last op_ segment
  let parentOptionId = null;
  let parentOpIdx = -1;
  for (let i = segments.length - 1; i >= 0; i--) {
    if (segments[i].startsWith('op_')) {
      parentOptionId = segments[i].replace('op_', '');
      parentOpIdx = i;
      break;
    }
  }
  if (!parentOptionId || parentOpIdx < 0) {
    return { parentOption: null, isNested: false };
  }

  // Build parentMgPathKey = everything before the last op_ segment
  const parentMgPathKey = segments.slice(0, parentOpIdx).join('__');
  const parentMg = modifierGroups[parentMgPathKey];
  const parentOpt = parentMg?.modifierOptions?.[parentOptionId];

  if (parentOpt) {
    return {
      parentOption: {
        _id: parentOptionId,
        ...parentOpt,
        originalPrice: parentOpt.originalPrice ?? parentOpt.price ?? 0,
        calculatedPrice: parentOpt.calculatedPrice ?? parentOpt.price ?? 0,
        priceType: parentOpt.priceType ?? null,
      },
      parentMgPathKey,
      isNested: true,
    };
  }
  return { parentOption: null, isNested: false };
};

// ---------------------------------------------------------------------------
// Validate all modifier groups recursively
// ---------------------------------------------------------------------------
export const validateAllModifierGroups = (
  productId,
  productMgIds,
  parentOptionPathKey,
  modifierGroups,
  menuMap
) => {
  const missingGroups = [];

  const validate = (mgIds, parentPK) => {
    (mgIds || []).forEach((mgId) => {
      const mgDef = menuMap?.modifierGroups?.[mgId];
      if (!mgDef) return;

      const pathKey = buildPathKey(productId, mgId, parentPK);
      const selected = modifierGroups[pathKey];
      const selectedOpts = selected?.modifierOptions || {};

      const multiplierCond = {
        applyConditionWithMultiplier: mgDef.applyConditionWithMultiplier,
        priceType: mgDef.priceType,
      };

      let optionsForCount;
      if (mgDef.conditionIsAppliedForProduct) {
        const instancePKs = findAllMgInstances(modifierGroups, mgId);
        optionsForCount = instancePKs.flatMap((pk) => {
          const inst = modifierGroups[pk];
          return inst ? Object.values(inst.modifierOptions ?? {}) : [];
        });
      } else {
        optionsForCount = Object.values(selectedOpts);
      }

      const selectedCount = getEffectiveOptionsCount(
        optionsForCount,
        mgDef.applyValidationToItemsActualQuantity,
        multiplierCond
      );

      let min = mgDef.min || 0;

      // Relax min to 1 for MGs with nested options (navigation layout only)
      // matching POS client ModifierGroups validation logic
      if (min > 0) {
        const hasNestedOptions = (mgDef.optionIds || []).some((optId) => {
          const optItem = menuMap?.items?.[optId];
          return optItem?.modifierGroupIds?.length > 0;
        });
        if (
          hasNestedOptions &&
          !mgDef.applyValidationToItemsActualQuantity &&
          mgDef.showNestedLayout !== 'popup'
        ) {
          min = 1;
        }
      }

      if (
        (min > 0 && selectedCount < min) ||
        !meetsModifierItemMinimums(mgDef, optionsForCount)
      ) {
        missingGroups.push(mgDef.name?.en || mgId);
      }

      const maxVal = Number(mgDef.max) || 0;
      if (maxVal > 0 && selectedCount > maxVal) {
        missingGroups.push(mgDef.name?.en || mgId);
      }

      const maxTotalItems = mgDef.maxTotalNumberOfModifierItems || 0;
      if (maxTotalItems > 0) {
        const totalItems = computeTotalItemCount(
          optionsForCount,
          mgDef.applyValidationToItemsActualQuantity,
          multiplierCond
        );
        if (totalItems > maxTotalItems) {
          missingGroups.push(mgDef.name?.en || mgId);
        }
      }

      const maxAnySingle = mgDef.maxAnySingleModifierItems || 0;
      if (maxAnySingle > 0) {
        for (const opt of optionsForCount) {
          if (opt == null) continue;
          const count = getEffectiveOptionCount(
            opt,
            mgDef.applyValidationToItemsActualQuantity,
            multiplierCond
          );
          if (count > maxAnySingle) {
            missingGroups.push(mgDef.name?.en || mgId);
            break;
          }
        }
      }

      // Recurse into each selected option's nested modifier groups
      Object.entries(selectedOpts).forEach(([optId]) => {
        const optItem = menuMap?.items?.[optId];
        const nestedMgIds = optItem?.modifierGroupIds || [];
        if (nestedMgIds.length > 0) {
          const optionPK = buildOptionPathKey(pathKey, optId);
          validate(nestedMgIds, optionPK);
        }
      });
    });
  };

  validate(productMgIds, parentOptionPathKey || null);

  return { valid: missingGroups.length === 0, missingGroups };
};

// ---------------------------------------------------------------------------
// Zero parent option prices when they have MATP children (ensures free chain
// parents are not charged; can be lost between preselection and add-item).
// ---------------------------------------------------------------------------
const getParentMgPathKeyFromChild = (childPathKey) => {
  const parts = childPathKey.split('__').filter(Boolean);
  if (parts.length < 4) return null;
  return parts.slice(0, -2).join('__');
};

export const applyZeroPricesForMatpParents = (groups) => {
  const result = { ...groups };
  const pathKeys = Object.keys(result).sort((a, b) => {
    const depthA = (a.match(/__mg_/g) || []).length;
    const depthB = (b.match(/__mg_/g) || []).length;
    return depthB - depthA;
  });

  for (const pathKey of pathKeys) {
    const group = result[pathKey];
    const opts = group?.modifierOptions || {};
    for (const [optId, opt] of Object.entries(opts)) {
      if (!opt || (opt.count ?? 0) <= 0) continue;
      const effectivePT = opt?.priceType ?? group?.priceType;
      if (effectivePT !== PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE)
        continue;
      const parentMgPathKey = getParentMgPathKeyFromChild(pathKey);
      const parentOptId = extractParentOptionId(pathKey);
      if (!parentOptId || !parentMgPathKey) continue;
      const parentGroup = result[parentMgPathKey];
      if (!parentGroup?.modifierOptions?.[parentOptId]) continue;
      const parentOpt = parentGroup.modifierOptions[parentOptId];
      if (parentOpt.price === 0) continue;
      result[parentMgPathKey] = {
        ...parentGroup,
        modifierOptions: {
          ...parentGroup.modifierOptions,
          [parentOptId]: {
            ...parentOpt,
            price: 0,
            actualQuantityWithMultiplier: 0,
          },
        },
      };
    }
  }
  return result;
};

// ---------------------------------------------------------------------------
// extractPathKeyMappings & prepareModifierGroupsForSDK
// ---------------------------------------------------------------------------
export const extractPathKeyMappings = (modifierGroups) => {
  const pathKeyMap = {};
  const pathKeysByGroupId = {};

  Object.entries(modifierGroups || {}).forEach(([key, group]) => {
    if (group && group._id) {
      if (!pathKeysByGroupId[group._id]) {
        pathKeysByGroupId[group._id] = [];
      }
      pathKeysByGroupId[group._id].push(group.pathKey || key);
    }
  });

  Object.entries(pathKeysByGroupId).forEach(([groupId, pathKeys]) => {
    pathKeyMap[groupId] = pathKeys.length === 1 ? pathKeys[0] : pathKeys;
  });

  return pathKeyMap;
};

export const prepareModifierGroupsForSDK = (modifierGroups) => {
  const prepared = {};
  // Store hideNameOnOrder and inOrderViewLayout values to re-inject after OrderHelper.addItem (which strips custom props)
  const customPropsMap = {};
  const freeMap = {};

  Object.entries(modifierGroups || {}).forEach(([key, group]) => {
    const modifierOptionsWithPathKey = {};
    const optionCustomProps = {};

    Object.entries(group.modifierOptions || {}).forEach(
      ([optionKey, option]) => {
        const optionPathKey =
          option.pathKey || `${key}__op_${option._id || optionKey}`;
        modifierOptionsWithPathKey[optionKey] = {
          ...option,
          pathKey: optionPathKey,
        };
        optionCustomProps[option._id || optionKey] = {
          hideNameOnOrder: option.hideNameOnOrder,
          inOrderViewLayout: option.inOrderViewLayout,
          actualQuantityWithMultiplier: option.actualQuantityWithMultiplier,
          freeCount: option.freeCount,
        };
      }
    );

    // Key by full pathKey (not _id) to avoid collisions when the same MG _id
    // appears multiple times with different options under different branches.
    customPropsMap[key] = {
      mgHideNameOnOrder: group.hideNameOnOrder,
      mgInOrderViewLayout: group.inOrderViewLayout,
      mgOptionIds: group.optionIds || null,
      mgSortParentOptionsByThisOptionsSorting:
        group.sortParentOptionsByThisOptionsSorting,
      mgGroupAllParentsByThisOption: group.groupAllParentsByThisOption,
      options: optionCustomProps,
    };

    // Track the `free` property per modifier group
    if (group.free != null) {
      freeMap[group._id || key] = group.free;
    }

    prepared[key] = {
      ...group,
      pathKey: key,
      modifierOptions: modifierOptionsWithPathKey,
    };
  });

  return { prepared, customPropsMap, freeMap };
};

// ---------------------------------------------------------------------------
// Re-inject pathKey, hideNameOnOrder, and inOrderViewLayout onto SDK result
// ---------------------------------------------------------------------------
const reinjectPathKeys = (
  orderResult,
  modifierPathKeys,
  customPropsMap,
  freeMap = {}
) => {
  if (!orderResult?.items?.length) return orderResult;

  // NOTE: OrderHelper.addItem PREPENDS the new item, so the newest item is at index 0
  const newItem = orderResult.items[0];
  if (newItem.modifierGroup) {
    const occurrenceCount = {};
    newItem.modifierGroup.forEach((mg) => {
      const groupId = mg._id || '';
      const pathKeyValue = modifierPathKeys[groupId];

      if (pathKeyValue) {
        if (Array.isArray(pathKeyValue)) {
          const occIdx = occurrenceCount[groupId] || 0;
          occurrenceCount[groupId] = occIdx + 1;
          mg.pathKey = pathKeyValue[occIdx] || pathKeyValue[0];
        } else {
          mg.pathKey = pathKeyValue;
        }
      }

      // Re-inject custom props. Look up by full pathKey first (avoids collision when the
      // same _id appears under different branches), fall back to _id for legacy entries.
      const propsData =
        (mg.pathKey ? customPropsMap[mg.pathKey] : null) ||
        customPropsMap[groupId];
      if (propsData) {
        mg.hideNameOnOrder = propsData.mgHideNameOnOrder;
        mg.inOrderViewLayout = propsData.mgInOrderViewLayout;
        if (propsData.mgOptionIds) {
          mg.optionIds = propsData.mgOptionIds;
        }
        if (propsData.mgSortParentOptionsByThisOptionsSorting != null) {
          mg.sortParentOptionsByThisOptionsSorting =
            propsData.mgSortParentOptionsByThisOptionsSorting;
        }
        if (propsData.mgGroupAllParentsByThisOption != null) {
          mg.groupAllParentsByThisOption =
            propsData.mgGroupAllParentsByThisOption;
        }
      }

      // Re-inject the `free` property so it persists after SDK processing
      if (freeMap[groupId] != null) {
        mg.free = freeMap[groupId];
      }

      // Also add pathKey, hideNameOnOrder and inOrderViewLayout to each option
      if (mg.modifierOptions) {
        (Array.isArray(mg.modifierOptions)
          ? mg.modifierOptions
          : Object.values(mg.modifierOptions)
        ).forEach((opt) => {
          opt.pathKey = `${mg.pathKey}__op_${opt._id}`;
          if (propsData?.options) {
            const optProps = propsData.options[opt._id];
            if (optProps) {
              opt.hideNameOnOrder = optProps.hideNameOnOrder;
              opt.inOrderViewLayout = optProps.inOrderViewLayout;
              if (optProps.actualQuantityWithMultiplier !== undefined) {
                opt.actualQuantityWithMultiplier =
                  optProps.actualQuantityWithMultiplier;
              }
              if (optProps.freeCount != null) {
                opt.freeCount = optProps.freeCount;
              }
            }
          }
        });
      }
    });
  }

  // Merge without overwriting existing entries (different items may share same MG _id)
  if (Object.keys(modifierPathKeys).length > 0) {
    const existingMappings = orderResult.metadata?.modifierPathKeys || {};
    const mergedMappings = { ...existingMappings };

    Object.entries(modifierPathKeys).forEach(([groupId, newPathKey]) => {
      if (mergedMappings[groupId]) {
        // Collision: same MG _id used by different items - store as array
        const existing = Array.isArray(mergedMappings[groupId])
          ? mergedMappings[groupId]
          : [mergedMappings[groupId]];
        const incoming = Array.isArray(newPathKey) ? newPathKey : [newPathKey];
        mergedMappings[groupId] = [...existing, ...incoming];
      } else {
        mergedMappings[groupId] = newPathKey;
      }
    });

    orderResult.metadata = {
      ...orderResult.metadata,
      modifierPathKeys: mergedMappings,
    };
  }

  return orderResult;
};

// ---------------------------------------------------------------------------
// addItemToPreviewOrder
// ---------------------------------------------------------------------------
export const addItemToPreviewOrder = ({
  orderData,
  selectedProduct,
  selectedCategory,
  modifierGroups,
  count,
  businessId,
}) => {
  const groupsWithZeroedParents = applyZeroPricesForMatpParents(modifierGroups);
  const { prepared, customPropsMap, freeMap } =
    prepareModifierGroupsForSDK(groupsWithZeroedParents);
  const modifierPathKeys = extractPathKeyMappings(prepared);

  // Compute item-level actualQuantityWithMultiplier from top-level MATP options
  const topLevelMultiplierOptions = Object.entries(prepared).flatMap(
    ([pathKey, group]) => {
      if (pathKey.includes('__op_')) return [];
      return Object.values(group.modifierOptions || {}).filter(
        (o) => o?.priceType === PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE
      );
    }
  );
  const itemActualQty =
    topLevelMultiplierOptions.length > 0
      ? computeParentActualQuantity(
          count,
          topLevelMultiplierOptions.map((o) => ({
            count: o.count ?? 1,
            originalPrice: o.originalPrice ?? 0,
          }))
        )
      : count;

  const addItemRequest = {
    item: { ...selectedProduct, actualQuantityWithMultiplier: itemActualQty },
    count,
    category: selectedCategory || undefined,
    modifierGroups: prepared,
    notes: selectedProduct?.notes || '',
    metadata: { modifierPathKeys },
  };

  let result;
  if (!orderData) {
    const createParams = {
      businessUuid: businessId || '',
      uuid: uuidv4(),
      displayNumber: 'Preview',
    };
    const newOrder = OrderHelper.createOrder(
      createParams,
      MOCK_STATUSES,
      MOCK_STATES
    );
    result = OrderHelper.addItem(
      newOrder,
      addItemRequest,
      MOCK_STATUSES,
      MOCK_STATES
    );
  } else {
    result = OrderHelper.addItem(
      orderData,
      addItemRequest,
      MOCK_STATUSES,
      MOCK_STATES
    );
  }

  reinjectPathKeys(result, modifierPathKeys, customPropsMap, freeMap);
  return result;
};

// ---------------------------------------------------------------------------
// removeItemFromPreviewOrder
// ---------------------------------------------------------------------------
export const removeItemFromPreviewOrder = (orderData, itemUuid) => {
  if (!orderData) return null;
  return OrderHelper.removeItem(
    orderData,
    itemUuid,
    MOCK_STATUSES,
    MOCK_STATES
  );
};

// ---------------------------------------------------------------------------
// Preselected options (mirrors POS client useOrderMenuSelection preselect logic)
// preselectOptions: { defaultSelection: boolean, options: { [key]: [{ _id, count }] } }
// key = mgId (first level) or "optionId_mgId" (nested under that option)
// ---------------------------------------------------------------------------

/** Return the preselect key for a pathKey (e.g. "mgId" or "optionId_mgId"). */
const getPreselectKeyFromPathKey = (pathKey) => {
  if (!pathKey) return null;
  const segments = pathKey.split('__');
  let lastMgIdx = -1;
  for (let i = 0; i < segments.length; i++) {
    if (segments[i] === 'mg' && segments[i + 1]) lastMgIdx = i;
  }
  if (lastMgIdx < 0) return null;
  const mgId = segments[lastMgIdx + 1];
  let opIdx = -1;
  for (let i = lastMgIdx - 1; i >= 0; i--) {
    if (segments[i] === 'op' && segments[i + 1]) {
      opIdx = i;
      break;
    }
  }
  if (opIdx >= 0 && segments[opIdx + 1])
    return `${segments[opIdx + 1]}_${mgId}`;
  return mgId;
};

/** True if this option is a preselected default for the product (so we should not remove it on click). */
export const isOptionPreselected = (product, pathKey, optionId) => {
  if (!product?.preselectOptions?.defaultSelection || !pathKey || !optionId)
    return false;
  const key = getPreselectKeyFromPathKey(pathKey);
  if (!key) return false;
  const list = product.preselectOptions?.options?.[key] || [];
  return list.some((opt) => opt._id === optionId);
};

// Direct port from POS client – checks sold-out status exactly as the POS does.
// soldOutPermanent → always sold out
// soldOutToday + soldOutDate >= now → sold out
// soldOutToday + soldOutDate < now (time passed) → active again
export const isItemSoldOut = (item) => {
  return (
    item?.soldOut?.status === 'soldOutPermanent' ||
    (item?.soldOut?.status === 'soldOutToday' &&
      new Date(item?.soldOut?.soldOutDate) >= new Date())
  );
};

const isOptionUnavailable = (option, menuMap) => {
  if (!option) return true;
  const item = menuMap?.items?.[option._id] || option;
  return isItemSoldOut(item);
};

// ---------------------------------------------------------------------------
// Recursive helper: apply preselections for options inside a MG, then recurse
// deeper. Mirrors POS client handlePreselectedModifierGroupOption depth logic.
// groups        – output map (mutated)
// preselectOpts – product.preselectOptions
// optKeys       – Object.keys(preselectOpts.options)
// menuMap       – full menu data
// parentMgPathKey – pathKey of the parent MG that owns parentModifierOptions
// parentModifierOptions – { [optId]: entry } of the parent MG (mutated for shouldZeroParentPrice)
// ---------------------------------------------------------------------------
/** Get preselections by new full-path key, with fallback to old optionId_nestedMgId key */
const getPreselectForPath = (
  preselectOpts,
  compositePath,
  nestedMgId,
  optionIdForOldKey
) => {
  const options = preselectOpts.options || {};
  const newKey = compositePath + '_' + nestedMgId;
  const oldKey =
    (optionIdForOldKey || compositePath.split('_').pop() || '') +
    '_' +
    nestedMgId;
  return options[newKey] ?? options[oldKey] ?? [];
};

const applyNestedPreselectionsRecursive = (
  groups,
  preselectOpts,
  menuMap,
  parentMgPathKey,
  parentModifierOptions,
  compositePathPrefix
) => {
  Object.keys(parentModifierOptions).forEach((optId) => {
    const optData = parentModifierOptions[optId];
    const optFullData = menuMap?.items?.[optId];
    if (!optFullData || (optFullData.modifierGroupIds?.length ?? 0) === 0)
      return;

    const pathForNext = compositePathPrefix + '_' + optId;

    (optFullData.modifierGroupIds || []).forEach((nestedMgId) => {
      let nestedPreselected = preselectOpts?.defaultSelection
        ? getPreselectForPath(preselectOpts, pathForNext, nestedMgId, optId)
        : [];

      if (
        nestedPreselected.length === 0 &&
        optFullData?.preselectOptions?.defaultSelection
      ) {
        nestedPreselected =
          optFullData.preselectOptions.options?.[nestedMgId] ?? [];
      }

      const nestedMg = menuMap?.modifierGroups?.[nestedMgId];

      if (
        nestedPreselected.length === 0 &&
        nestedMg?.preselectOptions?.defaultSelection
      ) {
        nestedPreselected =
          nestedMg.preselectOptions.options?.[nestedMgId] ?? [];
      }

      if (!nestedMg || nestedPreselected.length === 0) return;

      const nestedPathKey = `${parentMgPathKey}__op_${optId}__mg_${nestedMgId}`;
      if (groups[nestedPathKey]) return; // already filled

      const nestedOptions = {};
      const parentOptForCalc = {
        _id: optId,
        ...optData,
        originalPrice: optData?.originalPrice ?? optData?.price ?? 0,
        calculatedPrice: optData?.calculatedPrice ?? optData?.price ?? 0,
        priceType: optData?.priceType,
      };

      nestedPreselected.forEach((preOpt) => {
        if (!nestedMg.optionIds?.includes(preOpt._id)) return;
        const nestedOption = menuMap?.items?.[preOpt._id];
        if (!nestedOption || isOptionUnavailable(nestedOption, menuMap)) return;

        const { entry, priceResult } = buildOptionEntry({
          optionItem: { ...nestedOption, _id: preOpt._id },
          menuMap,
          modifierGroup: nestedMg,
          parentOption: parentOptForCalc,
          isNestedUnderParentOption: true,
          optionPathKey: `${nestedPathKey}__op_${preOpt._id}`,
        });
        entry.count = preOpt.count ?? 1;
        nestedOptions[preOpt._id] = entry;

        // Zero parent price when required (mirrors POS client)
        if (priceResult.shouldZeroParentPrice && parentModifierOptions[optId]) {
          const preselMultiplierChildren = Object.values(nestedOptions)
            .filter(
              (o) =>
                o?.priceType === PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE
            )
            .map((o) => ({
              count: o.count ?? 1,
              originalPrice: o.originalPrice ?? 0,
            }));
          const parentBaseAQ =
            parentModifierOptions[optId].calculatedActualQuantity ??
            parentModifierOptions[optId].actualQuantityWithMultiplier ??
            parentModifierOptions[optId].originalPrice ??
            0;
          const aqResult = computeActualQtyUpdate({
            parentMgPathKey,
            parentOptionId: optId,
            parentBaseActualQty: parentBaseAQ,
            multiplierChildren: preselMultiplierChildren,
            selectedModifierGroups: groups,
            menuItems: menuMap?.items ?? {},
            menuModifierGroups: menuMap?.modifierGroups ?? {},
          });
          parentModifierOptions[optId] = {
            ...parentModifierOptions[optId],
            price: 0,
            actualQuantityWithMultiplier: 0,
          };
          applyAncestorDispatch(groups, aqResult);
        }
      });

      if (Object.keys(nestedOptions).length > 0) {
        groups[nestedPathKey] = {
          ...nestedMg,
          pathKey: nestedPathKey,
          hideNameOnOrder:
            menuMap?.modifierGroups?.[nestedMgId]?.hideNameOnOrder ??
            nestedMg.hideNameOnOrder,
          inOrderViewLayout:
            menuMap?.modifierGroups?.[nestedMgId]?.inOrderViewLayout ??
            nestedMg.inOrderViewLayout,
          modifierOptions: nestedOptions,
        };

        // Recurse deeper (handles level 3, 4, 5, …)
        applyNestedPreselectionsRecursive(
          groups,
          preselectOpts,
          menuMap,
          nestedPathKey,
          nestedOptions,
          pathForNext + '_' + nestedMgId
        );
      }
    });
  });
};

/**
 * Build initial modifier groups from product preselectOptions (when product is selected).
 * Handles ALL nesting depths (mirrors POS client handlePreselectedModifierGroupOption).
 * Returns { [pathKey]: { ...mgDef, pathKey, modifierOptions: { [optId]: entry } } }.
 */
export const applyProductPreselections = (product, menuMap) => {
  const groups = {};
  if (!menuMap) return groups;

  const productMgIds = product?.modifierGroupIds || [];
  const preselectOpts = product?.preselectOptions;
  const handledMgIds = new Set();

  // --- Level 1: product-level modifier groups ---
  if (preselectOpts?.defaultSelection) {
    const optKeys = Object.keys(preselectOpts.options || {});

    optKeys.forEach((key) => {
      const isDirectMgKey = !key.includes('_') || productMgIds.includes(key);
      if (!isDirectMgKey || !productMgIds.includes(key)) return;

      const preselectedOptions = preselectOpts.options[key] || [];
      const mg = menuMap?.modifierGroups?.[key];
      if (!mg || preselectedOptions.length === 0) return;

      const pathKey = `product_${product._id}__mg_${key}`;
      const modifierOptions = {};

      preselectedOptions.forEach((preOpt) => {
        if (!mg.optionIds?.includes(preOpt._id)) return;
        const option = menuMap?.items?.[preOpt._id];
        if (!option || isOptionUnavailable(option, menuMap)) return;

        const { entry } = buildOptionEntry({
          optionItem: { ...option, _id: preOpt._id },
          menuMap,
          modifierGroup: mg,
          parentOption: null,
          isNestedUnderParentOption: false,
          optionPathKey: `${pathKey}__op_${preOpt._id}`,
        });
        entry.count = preOpt.count ?? 1;
        modifierOptions[preOpt._id] = entry;
      });

      if (Object.keys(modifierOptions).length > 0) {
        groups[pathKey] = {
          ...mg,
          pathKey,
          hideNameOnOrder:
            menuMap?.modifierGroups?.[key]?.hideNameOnOrder ??
            mg.hideNameOnOrder,
          inOrderViewLayout:
            menuMap?.modifierGroups?.[key]?.inOrderViewLayout ??
            mg.inOrderViewLayout,
          modifierOptions,
        };
        handledMgIds.add(key);

        // --- Level 2+ : recurse into all deeper nested preselections ---
        const pathFirstLevel = key;
        applyNestedPreselectionsRecursive(
          groups,
          preselectOpts,
          menuMap,
          pathKey,
          modifierOptions,
          pathFirstLevel
        );
      }
    });
  }

  // MG fallback: for first-level MGs not covered by product preselectOptions
  productMgIds.forEach((mgId) => {
    if (handledMgIds.has(mgId)) return;

    const mg = menuMap?.modifierGroups?.[mgId];
    if (!mg?.preselectOptions?.defaultSelection) return;

    const preselectedOptions = mg.preselectOptions.options?.[mgId] ?? [];
    if (preselectedOptions.length === 0) return;

    const pathKey = `product_${product._id}__mg_${mgId}`;
    const modifierOptions = {};

    preselectedOptions.forEach((preOpt) => {
      if (!mg.optionIds?.includes(preOpt._id)) return;
      const option = menuMap?.items?.[preOpt._id];
      if (!option || isOptionUnavailable(option, menuMap)) return;

      const { entry } = buildOptionEntry({
        optionItem: { ...option, _id: preOpt._id },
        menuMap,
        modifierGroup: mg,
        parentOption: null,
        isNestedUnderParentOption: false,
        optionPathKey: `${pathKey}__op_${preOpt._id}`,
      });
      entry.count = preOpt.count ?? 1;
      modifierOptions[preOpt._id] = entry;
    });

    if (Object.keys(modifierOptions).length > 0) {
      groups[pathKey] = {
        ...mg,
        pathKey,
        hideNameOnOrder:
          menuMap?.modifierGroups?.[mgId]?.hideNameOnOrder ??
          mg.hideNameOnOrder,
        inOrderViewLayout:
          menuMap?.modifierGroups?.[mgId]?.inOrderViewLayout ??
          mg.inOrderViewLayout,
        modifierOptions,
      };

      // Apply nested preselections for each preselected option (mirrors product preselect flow)
      applyNestedPreselectionsRecursive(
        groups,
        product?.preselectOptions || {},
        menuMap,
        pathKey,
        modifierOptions,
        mgId
      );
    }
  });

  return groups;
};

/**
 * Apply nested preselections when an option is selected (option has nested MGs).
 * Handles ALL nesting depths (mirrors POS client applyOptionPreselections).
 * Merges into currentGroups and returns new object.
 */
export const applyOptionPreselections = (
  product,
  menuMap,
  currentGroups,
  selectedOptionId,
  parentMgPathKey,
  selectedOptionData
) => {
  if (!menuMap) return currentGroups;

  const optionsMap = product?.preselectOptions?.options || {};

  const selectedOptionFullData = menuMap?.items?.[selectedOptionId];
  if (!selectedOptionFullData?.modifierGroupIds?.length) return currentGroups;

  // Build composite path from Redux-style path key
  const getCompositePathFromReduxPath = (reduxPath) => {
    const parts = reduxPath.split('__');
    const ids = [];
    parts.forEach((p) => {
      if (p.startsWith('mg_')) {
        ids.push(p.slice(3));
      } else if (p.startsWith('op_')) {
        ids.push(p.slice(3));
      }
    });
    return ids.join('_');
  };
  const compositePathToParent = getCompositePathFromReduxPath(parentMgPathKey);
  const pathForNested = compositePathToParent + '_' + selectedOptionId;

  const getPreselect = (nestedMgId) => {
    if (product?.preselectOptions?.defaultSelection) {
      const newKey = pathForNested + '_' + nestedMgId;
      const oldKey = selectedOptionId + '_' + nestedMgId;
      const fromProduct = optionsMap[newKey] ?? optionsMap[oldKey];
      if (fromProduct && fromProduct.length > 0) {
        return fromProduct;
      }
    }
    if (selectedOptionFullData?.preselectOptions?.defaultSelection) {
      const fromOption =
        selectedOptionFullData.preselectOptions.options?.[nestedMgId] ?? [];
      if (fromOption.length > 0) {
        return fromOption;
      }
    }
    const nestedMgData = menuMap?.modifierGroups?.[nestedMgId];
    if (nestedMgData?.preselectOptions?.defaultSelection) {
      return nestedMgData.preselectOptions.options?.[nestedMgId] ?? [];
    }
    return [];
  };

  const out = { ...currentGroups };

  (selectedOptionFullData.modifierGroupIds || []).forEach((nestedMgId) => {
    const preselectedOptions = getPreselect(nestedMgId);
    const nestedMg = menuMap?.modifierGroups?.[nestedMgId];
    if (!nestedMg || preselectedOptions.length === 0) return;

    const nestedMgPathKey = `${parentMgPathKey}__op_${selectedOptionId}__mg_${nestedMgId}`;
    if (out[nestedMgPathKey]) return; // already has selection for this path

    const nestedOptions = {};
    const parentOptData = {
      _id: selectedOptionId,
      ...selectedOptionData,
      originalPrice:
        selectedOptionData?.originalPrice ?? selectedOptionData?.price ?? 0,
      calculatedPrice:
        selectedOptionData?.calculatedPrice ?? selectedOptionData?.price ?? 0,
      priceType: selectedOptionData?.priceType,
    };

    preselectedOptions.forEach((preOpt) => {
      if (!nestedMg.optionIds?.includes(preOpt._id)) return;
      const nestedOption = menuMap?.items?.[preOpt._id];
      if (!nestedOption || isOptionUnavailable(nestedOption, menuMap)) return;

      const { entry, priceResult } = buildOptionEntry({
        optionItem: { ...nestedOption, _id: preOpt._id },
        menuMap,
        modifierGroup: nestedMg,
        parentOption: parentOptData,
        isNestedUnderParentOption: true,
        optionPathKey: `${nestedMgPathKey}__op_${preOpt._id}`,
      });
      entry.count = preOpt.count ?? 1;
      nestedOptions[preOpt._id] = entry;

      // Zero parent price when required (mirrors POS client)
      if (priceResult.shouldZeroParentPrice) {
        const parentMgGroup = out[parentMgPathKey];
        if (parentMgGroup?.modifierOptions?.[selectedOptionId]) {
          const preselMultiplierChildren = Object.values(nestedOptions)
            .filter(
              (o) =>
                o?.priceType === PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE
            )
            .map((o) => ({
              count: o.count ?? 1,
              originalPrice: o.originalPrice ?? 0,
            }));
          const parentBaseAQ =
            parentMgGroup.modifierOptions[selectedOptionId]
              .calculatedActualQuantity ??
            parentMgGroup.modifierOptions[selectedOptionId]
              .actualQuantityWithMultiplier ??
            parentMgGroup.modifierOptions[selectedOptionId].originalPrice ??
            0;
          const aqResult = computeActualQtyUpdate({
            parentMgPathKey,
            parentOptionId: selectedOptionId,
            parentBaseActualQty: parentBaseAQ,
            multiplierChildren: preselMultiplierChildren,
            selectedModifierGroups: out,
            menuItems: menuMap?.items ?? {},
            menuModifierGroups: menuMap?.modifierGroups ?? {},
          });
          out[parentMgPathKey] = {
            ...parentMgGroup,
            modifierOptions: {
              ...parentMgGroup.modifierOptions,
              [selectedOptionId]: {
                ...parentMgGroup.modifierOptions[selectedOptionId],
                price: 0,
                actualQuantityWithMultiplier: 0,
              },
            },
          };
          applyAncestorDispatch(out, aqResult);
        }
      }
    });

    if (Object.keys(nestedOptions).length > 0) {
      out[nestedMgPathKey] = {
        ...nestedMg,
        pathKey: nestedMgPathKey,
        hideNameOnOrder:
          menuMap?.modifierGroups?.[nestedMgId]?.hideNameOnOrder ??
          nestedMg.hideNameOnOrder,
        inOrderViewLayout:
          menuMap?.modifierGroups?.[nestedMgId]?.inOrderViewLayout ??
          nestedMg.inOrderViewLayout,
        modifierOptions: nestedOptions,
      };

      // Recurse deeper (handles level 3, 4, 5, …)
      applyNestedPreselectionsRecursive(
        out,
        product?.preselectOptions || {},
        menuMap,
        nestedMgPathKey,
        nestedOptions,
        pathForNested + '_' + nestedMgId
      );
    }
  });

  return out;
};

/**
 * Distribute a parent option's freeCount across merged lines using a drain
 * algorithm sorted by effective actual quantity descending.
 */
export const distributeMergedFreeCount = (parentFreeCount, mergeOptions) => {
  if (!parentFreeCount || mergeOptions.length === 0) {
    return { total: 0, perOption: mergeOptions.map(() => 0) };
  }

  const indexed = mergeOptions.map((opt, idx) => ({
    idx,
    aq: opt?.actualQuantityWithMultiplier || opt?.count || 0,
  }));

  indexed.sort((a, b) => b.aq - a.aq);

  const perOption = new Array(mergeOptions.length).fill(0);
  let remaining = parentFreeCount;

  for (const entry of indexed) {
    if (remaining <= 0) break;
    const assigned = Math.min(entry.aq, remaining);
    perOption[entry.idx] = assigned;
    remaining -= assigned;
  }

  return {
    total: perOption.reduce((s, v) => s + v, 0),
    perOption,
  };
};
